/**
 * GTM bootstrap for the SPA — the equivalent of the old server-rendered
 * <head> snippet (which we can't use here: index.html is static). Sets Google
 * Consent Mode v2 defaults (denied) synchronously, then loads the GTM container
 * from the server-provided id — but ONLY on the customer/shop host, never admin.
 *
 * Fail-safe by design: any error is swallowed so tracking can never break the app.
 */

type Gtag = (...args: unknown[]) => void

function dl(): unknown[] {
  const w = window as unknown as { dataLayer?: unknown[] }
  w.dataLayer = w.dataLayer || []
  return w.dataLayer
}

function gtag(): Gtag {
  return function () {
    // eslint-disable-next-line prefer-rest-params
    dl().push(arguments)
  }
}

// Consent is shared across the NetCell surfaces (enconf.com, netcell-it.de,
// zkm.netcell-it.de). localStorage is per-origin, so it CANNOT carry a decision
// between domains — we use two transport mechanisms on top of it:
//   1. URL handoff (?nc_consent=all|denied) — upstream sites append the visitor's
//      choice to the "order" link, so a customer coming from enconf.com/netcell-it.de
//      is never re-prompted here. Highest priority; consumed + stripped from the URL.
//   2. Cross-subdomain cookie on .netcell-it.de — carries the decision between
//      netcell-it.de and zkm.netcell-it.de (same registrable domain) and persists.
const CONSENT_KEY = 'nc_consent'

function registrableCookieDomain(): string {
  const parts = window.location.hostname.split('.')
  return parts.length >= 2 ? '.' + parts.slice(-2).join('.') : window.location.hostname
}

function setConsentCookie(v: string): void {
  try {
    const maxAge = 60 * 60 * 24 * 365 // 1 year
    document.cookie =
      `${CONSENT_KEY}=${encodeURIComponent(v)}; Max-Age=${maxAge}; Path=/; ` +
      `Domain=${registrableCookieDomain()}; SameSite=Lax; Secure`
  } catch {
    /* ignore */
  }
}

function readConsentCookie(): string | null {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + CONSENT_KEY + '=([^;]*)'))
    return m ? decodeURIComponent(m[1]) : null
  } catch {
    return null
  }
}

function persistConsent(v: 'all' | 'denied'): void {
  try {
    localStorage.setItem(CONSENT_KEY, v)
  } catch {
    /* private window */
  }
  setConsentCookie(v)
}

// Reads a ?nc_consent= handoff, persists it, and removes it from the URL so it
// isn't shared onward or kept in history. Returns the value if present.
function consumeConsentParam(): 'all' | 'denied' | null {
  try {
    const url = new URL(window.location.href)
    const p = url.searchParams.get(CONSENT_KEY)
    if (p !== 'all' && p !== 'denied') return null
    persistConsent(p)
    url.searchParams.delete(CONSENT_KEY)
    window.history.replaceState({}, '', url.pathname + url.search + url.hash)
    return p
  } catch {
    return null
  }
}

// Resolves the current decision from all sources (URL > localStorage > cookie),
// mirroring a cross-domain cookie into localStorage so consentDecided() agrees.
function resolveConsent(): 'all' | 'denied' | null {
  const fromUrl = consumeConsentParam()
  if (fromUrl) return fromUrl
  let v: string | null = null
  try {
    v = localStorage.getItem(CONSENT_KEY)
  } catch {
    /* ignore */
  }
  if (!v) {
    v = readConsentCookie()
    if (v) {
      try {
        localStorage.setItem(CONSENT_KEY, v) // mirror so the banner stays hidden
      } catch {
        /* ignore */
      }
    }
  }
  return v === 'all' ? 'all' : v === 'denied' ? 'denied' : null
}

// Consent Mode v2 defaults — MUST run before GTM loads, or GTM starts with
// undefined consent. Everything storage-related denied until the visitor opts in.
function setConsentDefaults(): void {
  const g = gtag()
  g('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  })
  if (resolveConsent() === 'all') {
    g('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
    })
  }
}

let loaded = false

function loadContainer(gtmId: string): void {
  if (loaded || !gtmId) return
  loaded = true
  dl().push({ 'gtm.start': Date.now(), event: 'gtm.js' })
  const s = document.createElement('script')
  s.async = true
  s.src = 'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(gtmId)
  document.head.appendChild(s)
  // noscript fallback (in case JS-less crawlers hit the SPA shell)
  const ns = document.createElement('noscript')
  const ifr = document.createElement('iframe')
  ifr.src = 'https://www.googletagmanager.com/ns.html?id=' + encodeURIComponent(gtmId)
  ifr.height = '0'
  ifr.width = '0'
  ifr.style.display = 'none'
  ifr.style.visibility = 'hidden'
  ns.appendChild(ifr)
  document.body.appendChild(ns)
}

/**
 * Reads the public web-config and, if a GTM id is configured AND we're on the
 * customer host, loads the container. Consent defaults are set immediately
 * regardless, so a later consent grant takes effect even before the container loads.
 */
export function bootGtm(): void {
  if (typeof window === 'undefined') return
  try {
    setConsentDefaults()
  } catch {
    /* never break the app on tracking setup */
  }
  fetch('/api/v1/public/webconfig')
    .then((r) => (r.ok ? r.json() : null))
    .then((body) => {
      const cfg = body?.data as { gtm_id?: string; customer_host?: string } | undefined
      const gtmId = cfg?.gtm_id
      if (!gtmId) return
      // Host gate: only the customer/shop site, so admin users aren't tracked.
      if (cfg?.customer_host && window.location.host !== cfg.customer_host) return
      loadContainer(gtmId)
    })
    .catch(() => {
      /* config unreachable → no tracking, no error */
    })
}

/**
 * Upgrades consent to "granted" and persists it (localStorage + cross-subdomain
 * cookie). Called by the fallback banner on "accept" AND automatically once a
 * portal user is authenticated (B2B portal, legitimate interest) — idempotent.
 */
export function grantConsent(): void {
  persistConsent('all')
  try {
    gtag()('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
    })
  } catch {
    /* ignore */
  }
}

/** Called by the fallback banner on "decline" — keeps storage denied. */
export function denyConsent(): void {
  persistConsent('denied')
}

/** Whether the visitor has already made a consent choice (banner dismissed). */
export function consentDecided(): boolean {
  try {
    if (localStorage.getItem(CONSENT_KEY) !== null) return true
  } catch {
    return true // can't store → don't nag
  }
  return readConsentCookie() !== null
}
